import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
  Button,
  Divider,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import {
  Add as AddIcon,
  Remove as RemoveIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  CalendarToday as DateIcon,
  Person as PersonIcon,
  Inventory as ItemIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { getApiUrl, API_ENDPOINTS } from '../../config/api';

interface Transaction {
  id: string;
  itemId: string | null;
  userId: string;
  type: 'in' | 'out' | 'delete';
  quantity: number;
  previousQuantity: number;
  newQuantity: number;
  notes: string;
  transactionDate: string;
  documentNumber?: string;
  salesRegistrationBatchId?: string | null;
  restockBatchId?: string | null;
  storeTransferBatchId?: string | null;
  supplierReturnBatchId?: string | null;
  storeTransferBatch?: { direction: string } | null;
  item?: {
    name: string;
    category: string;
  };
  user: {
    firstName: string;
    lastName: string;
  };
}

const VALID_CATEGORIES = ['business', 'office', 'exhibition', 'discovery_space', 'souvenirs', 'stickers', 'work_tools', 'workshop', 'kitchen'];

interface TransactionLogProps {
  activeCategory?: string;
}

const TransactionLog: React.FC<TransactionLogProps> = ({ activeCategory }) => {
  const { token, user, hasPermission } = useAuth();
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [categoryFilter, setCategoryFilter] = useState<string>(() =>
    (activeCategory && VALID_CATEGORIES.includes(activeCategory)) ? activeCategory : 'all'
  );
  const [limit, setLimit] = useState(20);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [clearDialogOpen, setClearDialogOpen] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [clearCategory, setClearCategory] = useState<string>('all');
  const [clearOperationType, setClearOperationType] = useState<string>('all');
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState(false);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const prevDebouncedRef = React.useRef(debouncedSearchTerm);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearchTerm(searchTerm), 350);
    return () => clearTimeout(t);
  }, [searchTerm]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearchTerm]);

  useEffect(() => {
    if (activeCategory && VALID_CATEGORIES.includes(activeCategory)) {
      setCategoryFilter(activeCategory);
      setPage(1);
    }
  }, [activeCategory]);

  useEffect(() => {
    const searchJustChanged = prevDebouncedRef.current !== debouncedSearchTerm;
    prevDebouncedRef.current = debouncedSearchTerm;
    fetchTransactions(searchJustChanged ? 1 : undefined);
  }, [typeFilter, categoryFilter, limit, page, debouncedSearchTerm]);

  const fetchTransactions = async (pageOverride?: number) => {
    const effectivePage = pageOverride ?? page;
    try {
      setLoading(true);
      setError(null);

      const offset = (effectivePage - 1) * limit;
      let url = `${getApiUrl(API_ENDPOINTS.STORAGE_TRANSACTIONS)}?limit=${limit}&offset=${offset}`;

      if (typeFilter !== 'all') {
        url += `&operationType=${encodeURIComponent(typeFilter)}`;
      }

      if (categoryFilter !== 'all') {
        url += `&category=${categoryFilter}`;
      }

      if (debouncedSearchTerm.trim()) {
        url += `&search=${encodeURIComponent(debouncedSearchTerm.trim())}`;
      }

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch transactions');
      }

      const data = await response.json();
      setTransactions(data.transactions);
      setTotal(data.total ?? 0);
    } catch (err) {
      console.error('Error fetching transactions:', err);
      setError('Failed to load transaction log');
    } finally {
      setLoading(false);
    }
  };

  const handleClearAllTransactions = async () => {
    // Validate password
    if (password !== 'admin321') {
      setPasswordError(true);
      return;
    }

    try {
      setClearing(true);
      setError(null);
      setPasswordError(false);

      const params = new URLSearchParams();
      if (clearCategory && clearCategory !== 'all') params.set('category', clearCategory);
      if (clearOperationType && clearOperationType !== 'all') params.set('operationType', clearOperationType);
      const url = params.toString()
        ? `${getApiUrl(API_ENDPOINTS.STORAGE_CLEAR_TRANSACTIONS)}?${params.toString()}`
        : getApiUrl(API_ENDPOINTS.STORAGE_CLEAR_TRANSACTIONS);
      const response = await fetch(url, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to clear transactions');
      }

      const data = await response.json();
      setClearDialogOpen(false);
      setPassword('');
      setClearCategory('all');
      setClearOperationType('all');
      setPage(1);
      await fetchTransactions();
      alert(`Sėkmingai išvalyti ${data.deletedCount} operacijų įrašai`);
    } catch (err) {
      console.error('Error clearing transactions:', err);
      setError(err instanceof Error ? err.message : 'Failed to clear transactions');
    } finally {
      setClearing(false);
    }
  };

  const isDeleteTransaction = (transaction: Transaction) => {
    return transaction.notes?.startsWith('IŠTRYNIMAS:') || false;
  };

  // Backend filters by operationType; no client-side re-filter
  const filteredTransactions = transactions;

  const getTransactionIcon = (transaction: Transaction) => {
    if (isDeleteTransaction(transaction)) return <DeleteIcon />;
    switch (transaction.type) {
      case 'in': return <AddIcon />;
      case 'out': return <RemoveIcon />;
      case 'delete': return <DeleteIcon />;
      default: return <AddIcon />;
    }
  };

  const getTransactionColor = (transaction: Transaction) => {
    if (isDeleteTransaction(transaction)) return 'warning';
    if (transaction.supplierReturnBatchId) return 'warning';
    if (transaction.storeTransferBatchId) return 'info';
    switch (transaction.type) {
      case 'in': return 'success';
      case 'out': return 'error';
      case 'delete': return 'warning';
      default: return 'default';
    }
  };

  const getTransactionText = (transaction: Transaction) => {
    if (isDeleteTransaction(transaction) || transaction.type === 'delete') return 'Ištrynimas';
    if (transaction.supplierReturnBatchId) return 'Grąžinta tiekėjui';
    if (transaction.storeTransferBatchId) {
      const dir = transaction.storeTransferBatch?.direction;
      if (dir === 'to_store') return 'Į parduotuvę';
      if (dir === 'to_warehouse') return 'Atgal į sandėliuką';
      if (dir === 'to_taken') return 'Paimta';
      if (dir === 'from_taken') return 'Grąžinta iš paimtos';
      return 'Perkėlimas';
    }
    if (transaction.type === 'out' && transaction.salesRegistrationBatchId) return 'Pardavimas';
    if (transaction.restockBatchId) return 'Pajamavimas';
    switch (transaction.type) {
      case 'in': return 'Pridėta';
      case 'out': return 'Paėmimas';
      default: return transaction.type;
    }
  };

  const getCategoryName = (category: string) => {
    switch (category) {
      case 'business': return 'Ūkinės prekės';
      case 'office': return 'Kanceliarinės prekės';
      case 'exhibition': return 'Ekspozicija';
      case 'discovery_space': return 'Atradimų erdvė';
      case 'souvenirs': return 'Suvenyrai';
      case 'stickers': return 'Lipdukai';
      case 'work_tools': return 'Darbo reikmenys';
      case 'workshop': return 'Asmeniniai';
      case 'kitchen': return 'Virtuvėlė';
      default: return category;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('lt-LT', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5" component="h2" sx={{ color: '#7b1fa2', fontWeight: 'bold' }}>
            📋 Sandėlio veiklos žurnalas
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() => fetchTransactions()}
              sx={{ 
                borderColor: '#7b1fa2', 
                color: '#7b1fa2',
                '&:hover': { borderColor: '#6a1b9a', backgroundColor: '#f3e5f5' }
              }}
            >
              Atnaujinti
            </Button>
            {hasPermission('storage.manage') && (
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() => {
                  setClearCategory(categoryFilter);
                  setClearOperationType(typeFilter);
                  setClearDialogOpen(true);
                }}
                sx={{ 
                  borderColor: '#d32f2f', 
                  color: '#d32f2f',
                  '&:hover': { borderColor: '#c62828', backgroundColor: '#ffebee' }
                }}
              >
                Išvalyti visus įrašus
              </Button>
            )}
          </Box>
        </Box>

        {/* Filters */}
        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
          <TextField
            label="Ieškoti operacijų"
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              )
            }}
            sx={{ minWidth: 250 }}
          />
          
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Operacijos tipas</InputLabel>
            <Select
              value={typeFilter}
              label="Operacijos tipas"
              onChange={(e) => {
                setTypeFilter(e.target.value);
                setPage(1);
              }}
            >
              <MenuItem value="all">Visi tipai</MenuItem>
              <MenuItem value="sale">Pardavimas</MenuItem>
              <MenuItem value="restock">Pajamavimas</MenuItem>
              <MenuItem value="transfer_to_store">Į parduotuvę</MenuItem>
              <MenuItem value="transfer_to_warehouse">Atgal į sandėliuką</MenuItem>
              <MenuItem value="transfer_to_taken">Paimta</MenuItem>
              <MenuItem value="transfer_from_taken">Grąžinta iš paimtos</MenuItem>
              <MenuItem value="return_to_supplier">Grąžinta tiekėjui</MenuItem>
              <MenuItem value="in">Pridėjimas</MenuItem>
              <MenuItem value="out">Paėmimas</MenuItem>
              <MenuItem value="delete">Ištrynimas</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Prekių kategorija</InputLabel>
            <Select
              value={categoryFilter}
              label="Prekių kategorija"
              onChange={(e) => {
                setCategoryFilter(e.target.value);
                setPage(1);
              }}
            >
              <MenuItem value="all">Visos kategorijos</MenuItem>
              <MenuItem value="business">Ūkinės prekės</MenuItem>
              <MenuItem value="office">Kanceliarinės prekės</MenuItem>
              <MenuItem value="exhibition">Ekspozicija</MenuItem>
              <MenuItem value="discovery_space">Atradimų erdvė</MenuItem>
              <MenuItem value="souvenirs">Suvenyrai</MenuItem>
              <MenuItem value="stickers">Lipdukai</MenuItem>
              <MenuItem value="work_tools">Darbo reikmenys</MenuItem>
              <MenuItem value="workshop">Asmeniniai</MenuItem>
              <MenuItem value="kitchen">Virtuvėlė</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Rodoma</InputLabel>
            <Select
              value={limit}
              label="Rodoma"
              onChange={(e) => {
                setLimit(Number(e.target.value));
                setPage(1);
              }}
            >
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={20}>20</MenuItem>
              <MenuItem value={50}>50</MenuItem>
              <MenuItem value={100}>100</MenuItem>
            </Select>
          </FormControl>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button
              size="small"
              variant="outlined"
              disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}
              sx={{ minWidth: 36 }}
            >
              Ankstesnis
            </Button>
            <Typography variant="body2" color="text.secondary" sx={{ px: 1 }}>
              {total > 0
                ? `Rodoma ${(page - 1) * limit + 1}–${Math.min(page * limit, total)} iš ${total}`
                : '—'}
            </Typography>
            <Button
              size="small"
              variant="outlined"
              disabled={total === 0 || page * limit >= total}
              onClick={() => setPage((p) => p + 1)}
              sx={{ minWidth: 36 }}
            >
              Kitas
            </Button>
          </Box>
        </Box>

        {/* Transactions List */}
        {filteredTransactions.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h6" color="text.secondary">
              {searchTerm || typeFilter !== 'all' || categoryFilter !== 'all' ? 'Operacijų nerasta pagal filtrus' : 'Operacijų dar nėra'}
            </Typography>
          </Box>
        ) : (
          <List>
            {filteredTransactions.map((transaction, index) => (
              <React.Fragment key={transaction.id}>
                <ListItem sx={{ py: 2 }}>
                  <ListItemIcon>
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      backgroundColor: isDeleteTransaction(transaction) ? '#fff3e0' : (transaction.type === 'in' ? '#e8f5e8' : '#ffebee'),
                      color: isDeleteTransaction(transaction) ? '#f57c00' : (transaction.type === 'in' ? '#2e7d32' : '#c62828')
                    }}>
                      {getTransactionIcon(transaction)}
                    </Box>
                  </ListItemIcon>
                  
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                          {transaction.item?.name || (isDeleteTransaction(transaction) ? transaction.notes.replace('IŠTRYNIMAS: ', '') : 'Nežinomas elementas')}
                        </Typography>
                        
                        <Chip
                          label={getTransactionText(transaction)}
                          color={getTransactionColor(transaction) as any}
                          size="small"
                        />
                        
                        <Chip
                          label={`${transaction.quantity} vnt`}
                          variant="outlined"
                          size="small"
                        />
                        
                        {transaction.item?.category && (
                          <Chip
                            label={getCategoryName(transaction.item.category)}
                            size="small"
                            sx={{ backgroundColor: '#f3e5f5', color: '#7b1fa2' }}
                          />
                        )}
                      </Box>
                    }
                    secondary={
                      <React.Fragment>
                        <span style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 8 }}>
                          <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                            <PersonIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                            <Typography variant="body2" color="text.secondary" component="span">
                              {transaction.user.firstName} {transaction.user.lastName}
                            </Typography>
                          </span>
                          
                          <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                            <DateIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                            <Typography variant="body2" color="text.secondary" component="span">
                              {formatDate(transaction.transactionDate)}
                            </Typography>
                          </span>
                        </span>
                        
                        {transaction.notes && (
                          <Typography variant="body2" color="text.secondary" component="span" 
                            sx={{ 
                              display: 'block',
                              fontStyle: 'italic',
                              backgroundColor: '#f5f5f5',
                              padding: 1,
                              borderRadius: 1,
                              mt: 1 
                            }}>
                            {isDeleteTransaction(transaction) 
                              ? transaction.notes.replace('IŠTRYNIMAS: ', '')
                              : `Pastaba: ${transaction.notes}`
                            }
                          </Typography>
                        )}
                        {transaction.documentNumber && (
                          <Typography variant="body2" color="text.secondary" component="span" sx={{ display: 'block', mt: 0.5 }}>
                            Važtaraštis: {transaction.documentNumber}
                          </Typography>
                        )}
                        <span style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
                          <Typography variant="caption" color="text.secondary" component="span">
                            Kiekis prieš: {transaction.previousQuantity} vnt
                          </Typography>
                          <Typography variant="caption" color="text.secondary" component="span">
                            →
                          </Typography>
                          <Typography variant="caption" color="text.secondary" component="span">
                            Kiekis po: {transaction.newQuantity} vnt
                          </Typography>
                        </span>
                      </React.Fragment>
                    }
                  />
                </ListItem>
                
                {index < filteredTransactions.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        )}

        {/* Summary */}
        {total > 0 && (
          <Paper sx={{ p: 2, mt: 2, backgroundColor: '#f5f5f5' }}>
            <Typography variant="body2" color="text.secondary">
              Rodoma {(page - 1) * limit + 1}–{Math.min(page * limit, total)} iš {total} operacijų
            </Typography>
          </Paper>
        )}
      </CardContent>

      {/* Clear All Confirmation Dialog */}
      <Dialog
        open={clearDialogOpen}
        onClose={() => {
          if (!clearing) {
            setClearDialogOpen(false);
            setPassword('');
            setClearCategory('all');
            setClearOperationType('all');
            setPasswordError(false);
          }
        }}
      >
        <DialogTitle>Išvalyti įrašus</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Galite pasirinkti, ar išvalyti visus įrašus, ar tik pasirinktos kategorijos ir (arba) operacijos tipo. Šis veiksmas negrįžtamas.
          </DialogContentText>
          <FormControl fullWidth sx={{ mb: 2 }} size="small">
            <InputLabel>Išvalyti kategoriją</InputLabel>
            <Select
              value={clearCategory}
              label="Išvalyti kategoriją"
              onChange={(e) => setClearCategory(e.target.value)}
              disabled={clearing}
            >
              <MenuItem value="all">Visos kategorijos</MenuItem>
              <MenuItem value="business">Ūkinės prekės</MenuItem>
              <MenuItem value="office">Kanceliarinės prekės</MenuItem>
              <MenuItem value="exhibition">Ekspozicija</MenuItem>
              <MenuItem value="discovery_space">Atradimų erdvė</MenuItem>
              <MenuItem value="souvenirs">Suvenyrai</MenuItem>
              <MenuItem value="stickers">Lipdukai</MenuItem>
              <MenuItem value="work_tools">Darbo reikmenys</MenuItem>
              <MenuItem value="workshop">Asmeniniai</MenuItem>
              <MenuItem value="kitchen">Virtuvėlė</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth sx={{ mb: 2 }} size="small">
            <InputLabel>Išvalyti pagal operacijos tipą</InputLabel>
            <Select
              value={clearOperationType}
              label="Išvalyti pagal operacijos tipą"
              onChange={(e) => setClearOperationType(e.target.value)}
              disabled={clearing}
            >
              <MenuItem value="all">Visi tipai</MenuItem>
              <MenuItem value="sale">Pardavimas</MenuItem>
              <MenuItem value="restock">Pajamavimas</MenuItem>
              <MenuItem value="transfer_to_store">Į parduotuvę</MenuItem>
              <MenuItem value="transfer_to_warehouse">Atgal į sandėliuką</MenuItem>
              <MenuItem value="transfer_to_taken">Paimta</MenuItem>
              <MenuItem value="transfer_from_taken">Grąžinta iš paimtos</MenuItem>
              <MenuItem value="return_to_supplier">Grąžinta tiekėjui</MenuItem>
              <MenuItem value="in">Pridėjimas</MenuItem>
              <MenuItem value="out">Paėmimas</MenuItem>
              <MenuItem value="delete">Ištrynimas</MenuItem>
            </Select>
          </FormControl>
          <TextField
            autoFocus
            margin="dense"
            label="Slaptažodis"
            type="password"
            fullWidth
            variant="outlined"
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              setPasswordError(false);
            }}
            error={passwordError}
            helperText={passwordError ? 'Neteisingas slaptažodis' : 'Įveskite slaptažodį patvirtinimui'}
            disabled={clearing}
            onKeyPress={(e) => {
              if (e.key === 'Enter' && password && !clearing) {
                handleClearAllTransactions();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setClearDialogOpen(false);
              setPassword('');
              setClearCategory('all');
              setClearOperationType('all');
              setPasswordError(false);
            }} 
            disabled={clearing}
          >
            Atšaukti
          </Button>
          <Button
            onClick={handleClearAllTransactions}
            color="error"
            variant="contained"
            disabled={clearing || !password}
            startIcon={clearing ? <CircularProgress size={20} /> : <DeleteIcon />}
          >
            {clearing ? 'Išvaloma...' : 'Išvalyti'}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default TransactionLog;

